'use client';

import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { forwardRef, type ReactNode } from 'react';

export type MarkTone = 'default' | 'success' | 'warning';

export interface MarkProps {
  /** Highlighted inline content. */
  children: ReactNode;
  /**
   * Visual tone of the highlight.
   * - `default` — uses theme primary accent (mint/glass-glow in v3 themes)
   * - `success` — uses theme success accent
   * - `warning` — uses theme warning accent
   */
  tone?: MarkTone;
  /** Optional sx overrides. */
  sx?: SxProps<Theme>;
}

/**
 * `Mark` — inline `<mark>` element styled with v3 glass-glow.
 *
 * Renders a subtle accent underline (no full-block background) so highlighted
 * text remains legible against any portal surface. Used by markdown to render
 * `<Mark>…</Mark>` tokens inside Credit Memo and Astor citation flows.
 *
 * Tone defaults to `default` which tracks `theme.palette.primary` — in v3 dark
 * themes that resolves to the mint/glass-glow accent.
 */
export const Mark = forwardRef<HTMLElement, MarkProps>(function Mark({ children, tone = 'default', sx }, ref) {
  // We compose theme-aware styles inline so consumer-supplied `sx` can still
  // win via the array form. `composeSx` only handles object bases.
  const baseSx = (theme: Theme) => {
    // Map tone → palette key. 'default' resolves to primary so it tracks the
    // host theme (mint accent in v3 dark, brand in light).
    const paletteKey = tone === 'default' ? 'primary' : tone;
    const color = theme.palette[paletteKey];
    const isDark = theme.palette.mode === 'dark';
    return {
      // Inline, no block-flow disruption.
      display: 'inline',
      // Transparent background — only the underline reads as highlight.
      backgroundColor: 'transparent',
      // Inherit color so legibility stays tied to surrounding text.
      color: 'inherit',
      // Glass-glow underline: 2px solid baseline + subtle inset glow band.
      textDecoration: 'underline',
      textDecorationColor: alpha(color.main, isDark ? 0.7 : 0.55),
      textDecorationThickness: '2px',
      textDecorationStyle: 'solid',
      textUnderlineOffset: '3px',
      // Faint surface tint so a paragraph of highlights still chunks visually
      // — alpha kept below 0.13 so it never reads as a full-block highlight.
      paddingInline: '1px',
      borderRadius: '2px',
      boxShadow: `inset 0 -1px 0 0 ${alpha(color.main, isDark ? 0.12 : 0.08)}`,
      transition: 'box-shadow 120ms ease, text-decoration-color 120ms ease',
    };
  };

  // Manual array compose — supports function-form base + any consumer sx shape.
  const sxArray: SxProps<Theme> = sx == null ? baseSx : [baseSx, ...(Array.isArray(sx) ? sx : [sx])];

  return (
    <Box ref={ref} component="mark" sx={sxArray}>
      {children}
    </Box>
  );
});

Mark.displayName = 'Mark';

export default Mark;
